import { ComponentType, lazy, LazyExoticComponent } from "react";
import { isNotNil } from "ramda-adjunct";

const RELOAD_FLAG = "lazyWithRetry:reloaded";

/**
 * Drop-in replacement for `React.lazy` that is resilient to stale chunks after a
 * deploy.
 *
 * When a new version is deployed the hashed chunk filenames change, so a client
 * that still has the old entry bundle loaded fails to fetch a lazily-imported
 * chunk ("Failed to fetch dynamically imported module"). This wrapper reloads
 * the page once on such a failure so the browser pulls the fresh asset manifest.
 *
 * A `sessionStorage` flag guards against an infinite reload loop when the import
 * is genuinely broken (rather than just stale): after one reload the error is
 * rethrown and surfaces to the error boundary as before. The flag is cleared on
 * any successful load so future stale-chunk reloads keep working.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const component = await factory();
      globalThis.sessionStorage?.removeItem(RELOAD_FLAG);
      return component;
    } catch (error) {
      const alreadyReloaded = globalThis.sessionStorage?.getItem(RELOAD_FLAG) === "true";
      if (!alreadyReloaded && isNotNil(globalThis.sessionStorage)) {
        globalThis.sessionStorage?.setItem(RELOAD_FLAG, "true");
        globalThis.location.reload();
        // Keep Suspense showing the fallback while the page reloads instead of
        // briefly flashing the error boundary.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
